import { useEffect, useRef, useState, useMemo } from 'react';

/**
 * Custom hook that triggers a callback when an element enters the viewport
 * @param {Object} options - Intersection Observer options
 * @returns {Object} { ref, inView }
 */
export const useInView = (options = {}) => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    
    // Memoize observer options to prevent unnecessary re-observations
    const observerOptions = useMemo(() => ({
        threshold: 0.15,
        rootMargin: '0px',
        ...options
    }), []);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setInView(true);
                // Once in view, we can stop observing (animations only play once)
                observer.unobserve(entry.target);
            }
        }, observerOptions);

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => {
            if (ref.current) {
                observer.unobserve(ref.current);
            }
        };
    }, [observerOptions]);

    return { ref, inView };
};
